import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { EXPOSED_STATUSES, formatMoney, ROLE_META } from '@accel/shared';
import { api } from '../lib/api';
import { useSession } from '../auth/AuthContext';
import type { DealSummary, Paged } from '../lib/types';
import {
  BarList,
  Donut,
  Funnel,
  Ring,
  SERIES,
  TrendArea,
} from '../components/charts';
import {
  advanceShare,
  byCounterparty,
  currenciesInPlay,
  exposureSlices,
  funnelStages,
  monthlyAdvances,
  settlementDiscipline,
  straightThrough,
  totals,
} from './dashboard-data';
import {
  Card,
  EmptyState,
  ErrorNote,
  Money,
  PageHead,
  PartyCell,
  Spinner,
  StatusTag,
  daysUntil,
  formatDate,
} from '../components/ui';

/**
 * The home screen, composed per role.
 *
 * Every role reads the same endpoint. The server already scopes the deal list
 * to what the session may see, so a supplier's dashboard and the operator's
 * are the same code over different rows — there is no second, role-shaped
 * query to keep in step, and no way for the console to show a figure the API
 * would not have returned.
 *
 * What differs per role is which question the page leads with: a supplier
 * wants to know when cash arrives, a payer what they owe and whether they pay
 * on time, an operator where deals are getting stuck.
 */
export function DashboardPage() {
  const session = useSession();
  const navigate = useNavigate();
  const role = session.membership.role;

  // One unpaginated read backs every tile. The seeded book is small; when it
  // grows this moves to a server-side aggregate endpoint rather than pulling
  // more rows into the browser.
  const query = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<Paged<DealSummary>>('/deals?pageSize=100&sort=dueDate&order=asc'),
  });

  const [chosenCurrency, setChosenCurrency] = useState<string | null>(null);

  if (query.isLoading) return <Spinner label="Loading your book" />;
  if (query.error) return <ErrorNote error={query.error} />;

  const deals = query.data?.data ?? [];
  const currencies = currenciesInPlay(deals);
  const currency = chosenCurrency ?? currencies[0] ?? 'USD';

  const sums = totals(deals, currency);
  const exposed = deals.filter((d) => EXPOSED_STATUSES.includes(d.status));
  const awaiting = deals.filter((d) => d.status === 'AWAITING_CONFIRMATION');
  const needsAction = deals.filter((d) =>
    ['SUBMITTED', 'REFERRED', 'ROUTED', 'DISPUTED'].includes(d.status),
  );
  const overdue = deals.filter((d) => d.status === 'OVERDUE');

  const trend = monthlyAdvances(deals, currency);
  const upcoming = deals
    .filter((d) => !['SETTLED', 'WRITTEN_OFF', 'CANCELLED', 'REJECTED'].includes(d.status))
    .slice(0, 8);

  const money = (value: bigint) => formatMoney(value, currency, { compact: true });

  /** The headline each role opens on. */
  const hero =
    role === 'SME'
      ? { figure: money(sums.advanced), label: 'released to you early', hint: 'Confirmed receivables turned into working capital, net of fees.' }
      : role === 'PAYER'
        ? { figure: money(sums.face - sums.settled), label: 'confirmed and owed', hint: 'What you have confirmed as payable, on your original terms.' }
        : role === 'LENDER'
          ? { figure: money(sums.exposure), label: 'deployed on this book', hint: 'Advances outstanding against confirmed receivables.' }
          : { figure: money(sums.face), label: 'receivables under management', hint: 'Origination to settlement, every party and programme in one view.' };

  return (
    <>
      <PageHead
        crumb={ROLE_META[role].name}
        title={`Good day, ${session.user.name.split(' ')[0]}`}
        sub={ROLE_META[role].description}
        actions={
          currencies.length > 1 ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span className="cell-t2">Currency</span>
              <select
                className="select"
                aria-label="Currency"
                value={currency}
                onChange={(e) => setChosenCurrency(e.target.value)}
              >
                {currencies.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </span>
          ) : undefined
        }
      />

      {/* The one figure this role came to see. Money is per currency, always:
          there is no FX rate in the platform, so a single "total" across the
          book would be an invented number. */}
      <div
        style={{
          background: 'var(--grad)',
          borderRadius: 'var(--r)',
          padding: '22px 24px',
          color: '#fff',
          marginBottom: 18,
          boxShadow: 'var(--shadow)',
        }}
      >
        <div style={{ fontSize: 12, letterSpacing: '.08em', textTransform: 'uppercase', opacity: 0.82 }}>
          {ROLE_META[role].name} · {currency}
        </div>
        <div className="num" style={{ fontSize: 34, fontWeight: 700, margin: '6px 0 2px' }}>
          {hero.figure}{' '}
          <span style={{ fontSize: 16, fontWeight: 500, opacity: 0.85 }}>{hero.label}</span>
        </div>
        <div style={{ fontSize: 13.5, opacity: 0.88, maxWidth: 620 }}>{hero.hint}</div>
      </div>

      <div className="kpi-row">
        <Kpi label="Live exposure" value={money(sums.exposure)} foot={`${exposed.length} deal${exposed.length === 1 ? '' : 's'} funded`} />
        <Kpi label="Needs action" value={String(needsAction.length)} foot="Submitted, referred, routed or disputed" />
        <Kpi label="Awaiting confirmation" value={String(awaiting.length)} foot="Sent to payers, not yet confirmed" />
        <Kpi
          label="Overdue"
          value={String(overdue.length)}
          foot={overdue.length ? 'Recourse or write-off decisions due' : 'Nothing past due'}
          tone={overdue.length ? 'rose' : undefined}
        />
      </div>

      {role === 'SME' ? (
        <SupplierBoard deals={deals} currency={currency} trend={trend} />
      ) : role === 'PAYER' ? (
        <PayerBoard deals={deals} currency={currency} />
      ) : role === 'LENDER' ? (
        <LenderBoard deals={deals} currency={currency} trend={trend} />
      ) : (
        <OperationsBoard deals={deals} currency={currency} trend={trend} />
      )}

      <Card title="Next to fall due" padded={false}>
        {upcoming.length === 0 ? (
          <EmptyState title="Nothing open" sub="Every deal in your book is closed." />
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Counterparty</th>
                  <th className="r">Face value</th>
                  <th>Due</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {upcoming.map((d) => {
                  const days = daysUntil(d.dueDate);
                  // A payer cares who is billing them; everyone else cares who pays.
                  const counterparty = role === 'PAYER' ? d.sme : d.payer;
                  return (
                    <tr key={d.id} onClick={() => navigate('/deals')}>
                      <td className="cell-t1">{d.reference}</td>
                      <td>
                        <PartyCell party={counterparty} />
                      </td>
                      <td className="r">
                        <Money minor={d.faceMinor} currency={d.currency} />
                      </td>
                      <td>
                        {formatDate(d.dueDate)}
                        <span
                          className="cell-t2"
                          style={{ display: 'block', color: days < 0 ? 'var(--rose)' : undefined }}
                        >
                          {days < 0 ? `${Math.abs(days)}d overdue` : `in ${days}d`}
                        </span>
                      </td>
                      <td>
                        <StatusTag status={d.status} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

// ------------------------------------------------------------------ boards --

function SupplierBoard({
  deals,
  currency,
  trend,
}: {
  deals: DealSummary[];
  currency: string;
  trend: ReturnType<typeof monthlyAdvances>;
}) {
  const share = advanceShare(deals, currency);

  return (
    <>
      <div className="grid-2">
        <Card title="Cash released to you" sub="net advances, by month">
          <TrendArea points={trend} />
        </Card>
        <Card title="How much of your book is working" sub="advanced against confirmed value">
          <Ring
            percent={share.percent}
            colour={SERIES[1]}
            caption={`${formatMoney(share.advanced, currency, { compact: true })} advanced against ${formatMoney(share.base, currency, { compact: true })} of live confirmed receivables. The rest is still moving through validation and confirmation.`}
          />
        </Card>
      </div>

      <div className="grid-2">
        <Card title="Where your liquidity comes from" sub="live receivables by payer">
          <BarList rows={byCounterparty(deals, 'payer', currency)} />
        </Card>
        <Card title="Concentration" sub="funded exposure by payer">
          <Donut
            slices={exposureSlices(deals, 'payer', currency)}
            centreValue={formatMoney(totals(deals, currency).exposure, currency, { compact: true })}
            centreLabel="outstanding"
          />
        </Card>
      </div>
    </>
  );
}

function PayerBoard({ deals, currency }: { deals: DealSummary[]; currency: string }) {
  const discipline = settlementDiscipline(deals);

  return (
    <>
      <div className="grid-2">
        <Card title="What you owe, by supplier" sub="confirmed and live">
          <BarList rows={byCounterparty(deals, 'sme', currency)} />
        </Card>
        <Card title="Settlement discipline" sub="paid on or before the due date">
          <Ring
            percent={discipline.percent}
            colour={discipline.percent >= 90 ? SERIES[1] : SERIES[2]}
            caption={
              discipline.settled === 0
                ? 'Nothing has settled yet, so there is no record to judge. Paying on time is what keeps supplier financing cheap.'
                : `Across ${discipline.settled} settled invoice${discipline.settled === 1 ? '' : 's'}. Predictable settlement is what keeps your suppliers' financing cheap, and your supply chain healthy.`
            }
          />
        </Card>
      </div>

      <div className="grid-2">
        <Card title="Where your invoices stand" sub="the queue that holds up supplier cash">
          <Funnel
            // Disjoint buckets rather than a funnel's nested stages: an
            // invoice sits in exactly one of these, so a share of the largest
            // would mean nothing.
            showShare={false}
            stages={[
              {
                key: 'awaiting',
                name: 'Awaiting you',
                count: deals.filter((d) => d.status === 'AWAITING_CONFIRMATION').length,
              },
              {
                key: 'confirmed',
                name: 'Confirmed',
                count: deals.filter((d) =>
                  ['CONFIRMED', 'PARTIALLY_CONFIRMED', 'ROUTED', 'OFFER_ACCEPTED'].includes(d.status),
                ).length,
              },
              {
                key: 'financed',
                name: 'Financed on it',
                count: deals.filter((d) => EXPOSED_STATUSES.includes(d.status)).length,
              },
              {
                key: 'settled',
                name: 'Settled by you',
                count: deals.filter((d) => d.status === 'SETTLED').length,
              },
            ]}
          />
        </Card>
        <Card title="Obligation concentration" sub="funded exposure by supplier">
          <Donut
            slices={exposureSlices(deals, 'sme', currency)}
            centreValue={formatMoney(totals(deals, currency).exposure, currency, { compact: true })}
            centreLabel="financed on you"
          />
        </Card>
      </div>
    </>
  );
}

function LenderBoard({
  deals,
  currency,
  trend,
}: {
  deals: DealSummary[];
  currency: string;
  trend: ReturnType<typeof monthlyAdvances>;
}) {
  const sums = totals(deals, currency);
  const yieldPct = sums.advanced > 0n ? Number((sums.fees * 10000n) / sums.advanced) / 100 : 0;

  return (
    <>
      <div className="grid-2">
        <Card title="Deployed by month" sub="advances funded">
          <TrendArea points={trend} colour={SERIES[3]} />
        </Card>
        <Card title="Fee yield on deployed capital" sub="discount fee over advances">
          <Ring
            percent={Math.min(100, yieldPct * 10)}
            colour={SERIES[1]}
            caption={`${yieldPct.toFixed(2)}% of ${formatMoney(sums.advanced, currency, { compact: true })} advanced, earned as discount fee. The ring is scaled ten-fold so a low single-digit yield is still readable.`}
          />
        </Card>
      </div>

      <div className="grid-2">
        <Card title="Obligor concentration" sub="funded exposure by payer">
          <Donut
            slices={exposureSlices(deals, 'payer', currency)}
            centreValue={formatMoney(sums.exposure, currency, { compact: true })}
            centreLabel="outstanding"
          />
        </Card>
        <Card title="Suppliers you are funding" sub="live receivables by supplier">
          <BarList rows={byCounterparty(deals, 'sme', currency)} colour={SERIES[3]} />
        </Card>
      </div>
    </>
  );
}

function OperationsBoard({
  deals,
  currency,
  trend,
}: {
  deals: DealSummary[];
  currency: string;
  trend: ReturnType<typeof monthlyAdvances>;
}) {
  const clean = straightThrough(deals);

  return (
    <>
      <div className="grid-2">
        <Card title="Lifecycle funnel" sub="how far deals get">
          <Funnel stages={funnelStages(deals)} />
        </Card>
        <Card title="Straight through" sub="cleared with no manual intervention">
          <Ring
            percent={clean.percent}
            colour={clean.percent >= 80 ? SERIES[1] : SERIES[2]}
            caption={`${clean.exceptions} of ${deals.length} deals needed manual review, were queried, or went past due. Everything else cleared on its own.`}
          />
        </Card>
      </div>

      <div className="grid-2">
        <Card title="Disbursed by month" sub="advances released">
          <TrendArea points={trend} />
        </Card>
        <Card title="Obligor concentration" sub="funded exposure by payer">
          <Donut
            slices={exposureSlices(deals, 'payer', currency)}
            centreValue={formatMoney(totals(deals, currency).exposure, currency, { compact: true })}
            centreLabel="outstanding"
          />
        </Card>
      </div>
    </>
  );
}

// A local KPI tile, so the dashboard can tint one without changing every
// other use of the shared component.
function Kpi({
  label,
  value,
  foot,
  tone,
}: {
  label: string;
  value: string;
  foot: string;
  tone?: 'rose';
}) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value num" style={tone === 'rose' ? { color: 'var(--rose)' } : undefined}>
        {value}
      </div>
      <div className="kpi-foot">{foot}</div>
    </div>
  );
}
